/**
 * FASWORLD view-transition coordination.
 *
 * Horizon enables cross-document View Transitions globally while
 * `transition_to_main_product` is on. FW product cards are not Horizon
 * `<product-card-link>` nodes, so unmarked navigations fall back to
 * `page-navigation` and keep the previous product-rail snapshot visible
 * (looks like a collection flash). This module:
 * 1. Marks FW card clicks for `product-image-transition`
 * 2. Skips the page-navigation fallback when page transitions are off
 * 3. Skips the transition for Shopify's native localization form: that
 *    submit flips `dir` ltr<->rtl and, on mobile, fires from inside the
 *    open header drawer (a fixed, full-viewport modal). Transitioning the
 *    whole captured document through a direction flip while a modal
 *    overlay is part of that snapshot is what mobile browsers were
 *    hanging on, leaving the new page unrevealed (white screen) — desktop
 *    has no drawer in the way, so it never hit this.
 */
(() => {
  if (window.__fwViewTransitionsInit) return;
  window.__fwViewTransitionsInit = true;

  const mainEl = () => document.getElementById('MainContent');

  const productTransitionEnabled = () => mainEl()?.dataset.productTransition === 'true';

  const pageTransitionEnabled = () => mainEl()?.dataset.pageTransitionEnabled === 'true';

  /**
   * @param {MouseEvent} event
   */
  const isModifiedClick = (event) =>
    event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;

  let localizationFormSubmitted = false;

  document.addEventListener('submit', (event) => {
    if (event.target instanceof HTMLFormElement && event.target.matches('.fw-locale')) {
      localizationFormSubmitted = true;
    }
  });

  document.addEventListener(
    'click',
    (event) => {
      if (event.defaultPrevented || isModifiedClick(event)) return;
      if (!(event.target instanceof Element)) return;
      if (!productTransitionEnabled()) return;

      const interactive = event.target.closest('button, input, label, select, textarea');
      if (interactive) return;

      const link = event.target.closest('a.fw-product-card__link');
      if (!(link instanceof HTMLAnchorElement)) return;
      if (link.getAttribute('aria-disabled') === 'true') return;

      const href = link.getAttribute('href');
      if (!href || href === '#' || href.startsWith('javascript:')) return;

      const media = link.querySelector('[data-view-transition-to-main-product]');
      if (!(media instanceof HTMLElement)) return;

      media.setAttribute('data-view-transition-type', 'product-image-transition');
      media.setAttribute('data-view-transition-triggered', 'true');
    },
    true
  );

  window.addEventListener('pageswap', (event) => {
    const { viewTransition } = /** @type {PageSwapEvent} */ (event);
    if (!(viewTransition instanceof ViewTransition)) return;

    if (localizationFormSubmitted) {
      viewTransition.skipTransition();
      return;
    }

    const triggered = document.querySelector('[data-view-transition-triggered]');

    // Keep intentional product image morphs; skip the root page-navigation
    // fallback that freezes homepage rails during PDP load.
    if (!pageTransitionEnabled() && !triggered) {
      viewTransition.skipTransition();
    }
  });
})();
